import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { LayoutDashboard, Truck, Settings, ScrollText, Menu, X, LogOut, Sun, Moon, Home, Satellite, Bell, Network, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
    { href: '/codelco/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/codelco/vehiculos', label: 'Vehículos', icon: Truck },
    { href: '/codelco/perfiles', label: 'Perfiles GPS', icon: Layers },
    { href: '/codelco/mapeo', label: 'Mapeo XML', icon: Network },
    { href: '/codelco/notificaciones', label: 'Notificaciones', icon: Bell, badge: true },
    { href: '/codelco/bitacora', label: 'Bitácora', icon: ScrollText },
    { href: '/codelco/config', label: 'Configuración', icon: Settings },
];

export function CodelcoLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [noLeidas, setNoLeidas] = useState(0);

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${API_URL}/api/codelco/notificaciones?solo_no_leidas=true&limit=1`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (r.ok) {
                    const d = await r.json();
                    setNoLeidas(d.no_leidas || 0);
                }
            } catch { /* ignore */ }
        };
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [location.pathname]);

    const Sidebar = ({ mobile = false }) => (
        <div className={`flex flex-col h-full ${mobile ? 'p-4' : 'p-6'} bg-card`}>
            <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Satellite className="h-5 w-5 text-white" />
                </div>
                <div>
                    <div className="font-black uppercase tracking-tight text-base leading-tight">Codelco</div>
                    <div className="text-xs text-muted-foreground -mt-0.5">Integrador Samtech</div>
                </div>
            </div>
            <nav className="flex-1 space-y-1">
                {navItems.map(item => {
                    const Icon = item.icon;
                    const active = location.pathname.startsWith(item.href);
                    const showBadge = item.badge && noLeidas > 0;
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                active
                                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            }`}
                            data-testid={`nav-${item.href.split('/').pop()}`}
                        >
                            <Icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {showBadge && <Badge className="bg-red-500 text-white text-xs h-5 px-1.5">{noLeidas}</Badge>}
                        </Link>
                    );
                })}
            </nav>
            <div className="space-y-2 pt-4 border-t">
                <div className="px-1 text-xs text-muted-foreground truncate">{user?.email}</div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/home')}>
                        <Home className="h-4 w-4 mr-1" /> Inicio
                    </Button>
                    <Button variant="outline" size="icon" onClick={toggleTheme} title="Tema">
                        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => { logout(); navigate('/login'); }} className="text-destructive" title="Salir" data-testid="btn-codelco-logout">
                        <LogOut className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-card border-b flex items-center px-4 gap-3">
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                    <Menu className="h-5 w-5" />
                </Button>
                <Satellite className="h-5 w-5 text-amber-500" />
                <span className="font-black uppercase text-sm tracking-tight">Codelco-Samtech</span>
            </header>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSidebarOpen(false)}>
                    <div className="absolute top-0 left-0 bottom-0 w-72 bg-card" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSidebarOpen(false)} className="absolute top-3 right-3">
                            <X className="h-5 w-5" />
                        </button>
                        <Sidebar mobile />
                    </div>
                </div>
            )}

            <div className="flex">
                {/* Desktop sidebar */}
                <aside className="hidden lg:block w-64 fixed top-0 left-0 bottom-0 border-r">
                    <Sidebar />
                </aside>

                <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 p-4 md:p-6">
                    {children}
                </main>
            </div>
        </div>
    );
}
